/**
 * Settings › Calendars — the calendars the Today grid shows and the Time
 * screen reads meetings from.
 *
 * Edited as a draft and saved with a button, not on every keystroke: each
 * address is a credential, pasting one is a deliberate act, and a half-pasted
 * URL written to the account and fetched would only produce an error.
 */

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useToast } from "@/context/ToastContext";
import { useProjects } from "@/hooks/useData";
import {
  CALENDAR_LOOKBACK_DAYS, DEFAULT_MIN_EVENT_MINUTES, MAX_CALENDARS, MIN_EVENT_CHOICES,
  useCalendarEvents, useCalendarSettings, type CalendarConnection,
} from "@/lib/calendarImport";
import { calendarColor } from "@/lib/calendarDay";
import { appMode } from "@/lib/pocketbase";
import { CALENDAR_PALETTE } from "@/lib/types";

const blank = (): CalendarConnection => ({ label: "", icalUrl: "", enabled: true, suggest: true });

export function CalendarSettingsPanel() {
  const toast = useToast();
  const qc = useQueryClient();
  const { settings, save, persistedOnServer } = useCalendarSettings();
  const { data: projects = [] } = useProjects();
  const { connected, query } = useCalendarEvents(projects);

  const [rows, setRows] = useState<CalendarConnection[]>(() =>
    settings.connections.length ? settings.connections : [blank()]);
  const [minMinutes, setMinMinutes] = useState(settings.minEventMinutes);

  // Adopt a copy that arrives from the account (another device saved) — but
  // only when nothing here has been edited, so it never eats a draft.
  const [baseline, setBaseline] = useState(() => JSON.stringify([settings.connections, settings.minEventMinutes]));
  useEffect(() => {
    const incoming = JSON.stringify([settings.connections, settings.minEventMinutes]);
    if (incoming === baseline) return;
    const draft = JSON.stringify([rows.filter((r) => r.icalUrl.trim() || r.label.trim()), minMinutes]);
    if (draft === baseline) {
      setRows(settings.connections.length ? settings.connections : [blank()]);
      setMinMinutes(settings.minEventMinutes);
    }
    setBaseline(incoming);
  }, [settings.connections, settings.minEventMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

  const kept = rows.filter((r) => r.icalUrl.trim());
  const dirty = JSON.stringify([kept, minMinutes]) !== JSON.stringify([settings.connections, settings.minEventMinutes]);
  const set = (i: number, patch: Partial<CalendarConnection>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  function onSave() {
    const bad = kept.find((r) => !/^(https|webcals?):\/\//i.test(r.icalUrl.trim()));
    if (bad) { toast("Calendar addresses start with https:// or webcal://"); return; }
    const connections = kept.map((r, i) => ({
      label: r.label.trim() || `Calendar ${i + 1}`, icalUrl: r.icalUrl.trim(), enabled: r.enabled,
      suggest: r.suggest !== false, ...(r.color ? { color: r.color } : {}),
    }));
    save({ ...settings, connections, minEventMinutes: minMinutes });
    setRows(connections.length ? connections : [blank()]);
    toast(connections.length ? "Calendars saved" : "Calendars removed");
  }

  if (appMode === "demo") {
    return <p style={hint}>Only available on the live site — demo mode has no calendars to read.</p>;
  }

  const errors = query.data?.errors ?? [];
  const failure = query.error instanceof Error ? query.error.message : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
      <p style={hint}>
        Every calendar that's on shows on the Today screen. Those with <b>Suggest in Time</b> also offer
        their meetings from the last {CALENDAR_LOOKBACK_DAYS} days on Time › Today as one-click entries —
        turn it off for a personal calendar you want to see but never bill. Suggestions leave out all-day
        events, anything marked free, cancelled events, meetings you declined, and anything already covered
        by a logged entry. Any calendar that offers an <b>.ics / iCal</b> link works, up to {MAX_CALENDARS}.
      </p>

      {rows.map((row, i) => (
        <div key={i} style={box}>
          {/* One thing per line, so nothing reflows from row to row as the
              window narrows: name, its switches, its colour, its address. */}
          <input
            value={row.label}
            onChange={(e) => set(i, { label: e.target.value })}
            placeholder="Label (e.g. Work)"
            aria-label={`Calendar ${i + 1} label`}
            style={{ ...input, width: 240 }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={() => set(i, { suggest: row.suggest === false })}
              aria-pressed={row.suggest !== false}
              disabled={!row.enabled}
              title="Offer this calendar's meetings as one-click entries on Time › Today"
              style={{ ...chip, ...(row.suggest !== false && row.enabled ? chipOn : null), opacity: row.enabled ? 1 : 0.5 }}
            >{row.suggest !== false ? "✓ " : ""}Suggest in Time</button>
            <button
              onClick={() => set(i, { enabled: !row.enabled })}
              aria-pressed={row.enabled}
              style={{ ...chip, ...(row.enabled ? chipOn : null) }}
            >{row.enabled ? "On" : "Off"}</button>
            <button
              onClick={() => setRows((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : [blank()]))}
              style={{ ...chip, color: "var(--bad)" }}
            >Remove</button>
          </div>
          {/* Its colour on the Today grid. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Colour</span>
            <div role="radiogroup" aria-label={`Calendar ${i + 1} colour`} style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {CALENDAR_PALETTE.map((c) => {
                const on = calendarColor(row, i) === c;
                return (
                  <button key={c} role="radio" aria-checked={on} aria-label={c} title="Colour on the Today screen"
                    onClick={() => set(i, { color: c })}
                    style={{
                      width: 18, height: 18, borderRadius: 5, padding: 0, cursor: "pointer", background: c,
                      border: "none", boxShadow: on ? "0 0 0 2px var(--surface2), 0 0 0 4px var(--ink2)" : "none",
                    }} />
                );
              })}
            </div>
          </div>
          <input
            value={row.icalUrl}
            onChange={(e) => set(i, { icalUrl: e.target.value })}
            placeholder="https://… .ics  or  webcal://…"
            aria-label={`Calendar ${i + 1} address`}
            spellCheck={false}
            autoComplete="off"
            className="mono"
            style={{ ...input, width: "100%", fontSize: 12 }}
          />
        </div>
      ))}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {rows.length < MAX_CALENDARS && (
          <button onClick={() => setRows((prev) => [...prev, blank()])} style={chip}>+ Add calendar</button>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: "var(--muted)" }} title="Only affects Time's meeting suggestions — the Today screen shows every event">Time suggestions skip events shorter than</span>
        {MIN_EVENT_CHOICES.map((m) => (
          <button key={m} onClick={() => setMinMinutes(m)} style={{ ...chip, ...(minMinutes === m ? chipOn : null) }}>
            {m === 0 ? "Show all" : `${m}m`}{m === DEFAULT_MIN_EVENT_MINUTES ? " ·" : ""}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={onSave} disabled={!dirty} style={{ ...primary, opacity: dirty ? 1 : 0.5, cursor: dirty ? "pointer" : "default" }}>
          Save
        </button>
        {connected && (
          <button
            onClick={() => void qc.invalidateQueries({ queryKey: ["calendar-events"] })}
            disabled={query.isFetching}
            style={chip}
          >{query.isFetching ? "Syncing…" : "Sync now"}</button>
        )}
        <span style={{ fontSize: 12, color: "var(--muted)" }}>
          {connected && query.data && !failure
            ? `${errors.length ? "Partly synced" : "Synced"} ${new Date(query.data.fetchedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · ${query.data.events.length} event${query.data.events.length === 1 ? "" : "s"} in range`
            : null}
        </span>
      </div>

      {(failure || errors.length > 0) && (
        <div style={warn}>
          {failure && <div>{failure}</div>}
          {errors.map((e, i) => <div key={i}><b>{e.label}:</b> {e.message}</div>)}
        </div>
      )}

      {persistedOnServer === false && (
        <div style={warn}>
          Saved in this browser only — your PocketBase <code>users</code> collection has no <code>calendar</code> field,
          so the account did not keep these and your other devices won't see them. Add a JSON field
          named <code>calendar</code> to <code>users</code> in the PocketBase admin, then press Save again.
        </div>
      )}

      <details style={{ fontSize: 12.5, color: "var(--muted)" }}>
        <summary style={{ cursor: "pointer", color: "var(--ink2)" }}>Where do I find my calendar's .ics link?</summary>
        <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.6 }}>
          <li><b>Google Calendar:</b> Settings → pick the calendar → Integrate calendar → <i>Secret address in iCal format</i>.</li>
          <li><b>Outlook / Microsoft 365:</b> Settings → Calendar → Shared calendars → Publish a calendar → choose the calendar and “Can view all details” → copy the <i>ICS</i> link.</li>
          <li><b>iCloud:</b> in the Calendar app, share the calendar → turn on <i>Public Calendar</i> → copy the webcal:// link.</li>
          <li><b>Anything else</b> (Fastmail, Proton, a CRM, a booking tool): look for “iCal”, “ICS” or “subscribe” — any link ending in or serving .ics works.</li>
        </ul>
        <p style={{ margin: "8px 0 0" }}>
          Treat these links like passwords: anyone who has one can read that calendar. They're stored on your account,
          visible only to you.
        </p>
      </details>
    </div>
  );
}

const hint: React.CSSProperties = { margin: 0, fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5 };
const box: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 8, padding: 12, borderRadius: 10,
  border: "1px solid var(--line)", background: "var(--surface2)",
};
const input: React.CSSProperties = {
  maxWidth: "100%", padding: "8px 11px", borderRadius: 8, fontSize: 13, boxSizing: "border-box",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const chip: React.CSSProperties = {
  padding: "6px 12px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
};
const chipOn: React.CSSProperties = {
  border: "1px solid var(--accentLine)", background: "var(--accentTint)", color: "var(--accent)",
};
const primary: React.CSSProperties = {
  padding: "7px 16px", borderRadius: 999, border: "none", fontSize: 12.5, fontWeight: 600,
  background: "var(--accent)", color: "var(--onAccent)",
};
const warn: React.CSSProperties = {
  padding: 10, borderRadius: 8, fontSize: 12.5, lineHeight: 1.5,
  border: "1px solid color-mix(in srgb, var(--bad) 35%, transparent)",
  background: "color-mix(in srgb, var(--bad) 7%, transparent)", color: "var(--ink2)",
};
